import type { ValidationErrorObj } from '@/models/error';
import { getRequiredErrorMessage, getEmailRequiredAndFormatErrorMessage } from '@/wabunka-src/common/utils';

export interface ItineraryContactForm {
  slug: string;
  url: string;
  itineraryTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  guestType: 'individual' | 'company';
  companyName: string;
  nationality: string;
  phoneNumber: string;
  destination: string;
  duration: string;
  budget: string;
  numberAndAgeOfParticipants: string;
  message: string;
}

export interface ItineraryContactFormRequestBody {
  slug: string;
  url: string;
  itineraryTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  companyName: string;
  nationality: string;
  phoneNumber: string;
  destination: string;
  duration: string;
  budget: string;
  numberAndAgeOfParticipants: string;
  message: string;
}

/**
 * 英語版：旅程問い合わせフォームのデフォルトオブジェクトを返却する
 * @returns {ItineraryContactForm} - フォームのデフォルトオブジェクト
 */
export const createDefaultItineraryContactForm = (): ItineraryContactForm => ({
  slug: '',
  url: '',
  itineraryTitle: '',
  firstName: '',
  lastName: '',
  email: '',
  guestType: 'individual',
  companyName: '',
  nationality: '',
  phoneNumber: '',
  destination: '',
  duration: '',
  budget: '',
  numberAndAgeOfParticipants: '',
  message: '',
});

export const buildItineraryContactFormRequestBody = (form: ItineraryContactForm): ItineraryContactFormRequestBody => {
  const companyName = form.guestType === 'individual' ? '' : form.companyName;
  return {
    slug: form.slug,
    url: form.url,
    itineraryTitle: form.itineraryTitle,
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    companyName: companyName,
    nationality: form.nationality,
    phoneNumber: form.phoneNumber,
    destination: form.destination,
    duration: form.duration,
    budget: form.budget,
    numberAndAgeOfParticipants: form.numberAndAgeOfParticipants,
    message: form.message,
  };
};

/**
 * 英語版：旅程問い合わせフォームのバリデータ
 * @param {ItineraryContactForm} form - フォームの入力値
 */
export const validateItineraryContactForm = (form: ItineraryContactForm) => {
  let message: string | undefined;
  const errors: ValidationErrorObj[] = [];

  message = getRequiredErrorMessage(form.itineraryTitle);
  if (message) errors.push({ fieldName: 'itineraryTitle', message });

  message = getRequiredErrorMessage(form.lastName);
  if (message) errors.push({ fieldName: 'lastName', message });

  message = getRequiredErrorMessage(form.firstName);
  if (message) errors.push({ fieldName: 'firstName', message });

  message = getEmailRequiredAndFormatErrorMessage(form.email);
  if (message) errors.push({ fieldName: 'email', message });

  if (form.guestType === 'company') {
    message = getRequiredErrorMessage(form.companyName);
    if (message) errors.push({ fieldName: 'companyName', message });
  }

  message = getRequiredErrorMessage(form.destination);
  if (message) errors.push({ fieldName: 'destination', message });

  message = getRequiredErrorMessage(form.duration);
  if (message) errors.push({ fieldName: 'duration', message });

  message = getRequiredErrorMessage(form.budget);
  if (message) errors.push({ fieldName: 'budget', message });

  message = getRequiredErrorMessage(form.numberAndAgeOfParticipants);
  if (message) errors.push({ fieldName: 'numberAndAgeOfParticipants', message });

  message = getRequiredErrorMessage(form.message);
  if (message) errors.push({ fieldName: 'message', message });

  return errors;
};
